import { useState, useEffect, useRef } from "react";
import { initializeApp } from "firebase/app";
import {
  getFirestore,
  collection,
  addDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  doc,
  query,
  orderBy,
  Timestamp
} from "firebase/firestore";

/* ================= FIREBASE ================= */
const firebaseConfig = {
  apiKey: "AIzaSyCpVpnHyOT6YlBPENpO9cdIMggofnecPFI",
  authDomain: "interview-defe5.firebaseapp.com",
  projectId: "interview-defe5",
};

const app = initializeApp(firebaseConfig);
const db = getFirestore(app);

/* ================= MAIN ================= */
export default function Interview() {
  const [entries, setEntries] = useState([]);
  const [expanded, setExpanded] = useState({});
  const [editId, setEditId] = useState(null);
  const [localAnswers, setLocalAnswers] = useState({});
  const saveTimeout = useRef({});

  const [form, setForm] = useState({
    name: "",
    company: "",
    date: "",
    result: "Selected",
    questions: ""
  });

  /* ================= LOAD ================= */
  useEffect(() => {
    const load = async () => {
      const q = query(collection(db, "interviews"), orderBy("date", "desc"));
      const snap = await getDocs(q);

      const data = snap.docs.map(d => {
        const item = d.data();

        if (typeof item.questions === "string") {
          item.questions = item.questions
            .split("\n")
            .filter(q => q.trim() !== "")
            .map(q => ({ q, a: "" }));
        }

        return { id: d.id, ...item };
      });

      setEntries(data);
    };

    load();
  }, []);

  /* ================= SAVE ================= */
  const handleSubmit = async () => {
    if (!form.name || !form.date) return;

    let questionsArray = form.questions
      .split("\n")
      .filter(q => q.trim() !== "")
      .map(q => ({ q, a: "" }));

    if (editId) {
      const existing = entries.find(e => e.id === editId);
      if (existing?.questions) {
        questionsArray = questionsArray.map(q => {
          const old = existing.questions.find(x => x.q === q.q);
          return old ? old : q;
        });
      }
    }

    const payload = {
      name: form.name,
      company: form.company,
      result: form.result,
      date: Timestamp.fromDate(new Date(form.date)),
      questions: questionsArray
    };

    if (editId) {
      await updateDoc(doc(db, "interviews", editId), payload);
      setEntries(prev =>
        prev.map(e => (e.id === editId ? { ...e, ...payload } : e))
      );
      setEditId(null);
    } else {
      const ref = await addDoc(collection(db, "interviews"), payload);
      setEntries(prev => [...prev, { id: ref.id, ...payload }]);
    }

    setForm({
      name: "",
      company: "",
      date: "",
      result: "Selected",
      questions: ""
    });
  };

  /* ================= EDIT ================= */
  const handleEdit = (e) => {
    setEditId(e.id);

    const questionsText = (e.questions || [])
      .map(q => q.q)
      .join("\n");

    setForm({
      name: e.name,
      company: e.company || "",
      date: e.date?.toDate
        ? e.date.toDate().toISOString().split("T")[0]
        : e.date,
      result: e.result,
      questions: questionsText
    });

    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  /* ================= DELETE ================= */
  const handleDelete = async (id) => {
    await deleteDoc(doc(db, "interviews", id));
    setEntries(prev => prev.filter(e => e.id !== id));
  };

  /* ================= TOGGLE ================= */
  const toggle = (id, index) => {
    const key = id + "-" + index;
    setExpanded(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  /* ================= AUTO SAVE ================= */
  const saveAnswer = (entryId, qIndex, value) => {
    const key = entryId + "-" + qIndex;

    if (saveTimeout.current[key]) {
      clearTimeout(saveTimeout.current[key]);
    }

    saveTimeout.current[key] = setTimeout(async () => {
      const entry = entries.find(e => e.id === entryId);
      if (!entry) return;

      const updatedQuestions = [...(entry.questions || [])];
      updatedQuestions[qIndex].a = value;

      await updateDoc(doc(db, "interviews", entryId), {
        questions: updatedQuestions
      });

      setEntries(prev =>
        prev.map(e =>
          e.id === entryId ? { ...e, questions: updatedQuestions } : e
        )
      );
    }, 500);
  };

  const formatDate = (d) =>
    d?.toDate ? d.toDate().toISOString().split("T")[0] : d;

  /* ================= UI ================= */
  return (
    <div style={styles.container}>
      <h2 style={styles.title}>📋 Interview Tracker</h2>

      {/* FORM */}
      <div style={styles.card}>
        <h3>{editId ? "✏️ Edit Interview" : "➕ Add Interview"}</h3>

        <div style={styles.formGrid}>
          <input
            placeholder="Name"
            value={form.name}
            onChange={e => setForm({ ...form, name: e.target.value })}
            style={styles.input}
          />

          <input
            placeholder="Company"
            value={form.company}
            onChange={e => setForm({ ...form, company: e.target.value })}
            style={styles.input}
          />

          <input
            type="date"
            value={form.date}
            onChange={e => setForm({ ...form, date: e.target.value })}
            style={styles.input}
          />
        </div>

        <textarea
          placeholder="Questions (one per line)"
          value={form.questions}
          onChange={e => setForm({ ...form, questions: e.target.value })}
          style={styles.textarea}
        />

        <button onClick={handleSubmit} style={styles.primaryBtn}>
          {editId ? "Update" : "Add"}
        </button>
      </div>

      {/* LIST */}
      {entries.map(e => (
        <div key={e.id} style={styles.card}>
          <div style={styles.header}>
            <div>
              <h3>{e.name}</h3>
              <p style={styles.subText}>
                {e.company || "-"} | {formatDate(e.date)}
              </p>
            </div>

            <div>
              <button style={styles.editBtn} onClick={() => handleEdit(e)}>
                Edit
              </button>
              <button style={styles.deleteBtn} onClick={() => handleDelete(e.id)}>
                Delete
              </button>
            </div>
          </div>

          {(e.questions || []).map((q, i) => {
            const key = e.id + "-" + i;

            return (
              <div key={i} style={styles.questionBox}>
                <div
                  style={styles.question}
                  onClick={() => toggle(e.id, i)}
                >
                  👉 {q.q}
                </div>

                {expanded[key] && (
                  <textarea
                    value={
                      localAnswers[key] !== undefined
                        ? localAnswers[key]
                        : q.a || ""
                    }
                    onChange={(ev) => {
                      setLocalAnswers(prev => ({
                        ...prev,
                        [key]: ev.target.value
                      }));
                      saveAnswer(e.id, i, ev.target.value);
                    }}
                    style={styles.answerBox}
                  />
                )}
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}

/* ================= STYLES ================= */
const styles = {
  container: {
    maxWidth: "900px",
    margin: "auto",
    padding: "20px",
    fontFamily: "Arial"
  },
  title: {
    textAlign: "center",
    marginBottom: "20px"
  },
  card: {
    background: "#fff",
    padding: "20px",
    borderRadius: "12px",
    boxShadow: "0 4px 10px rgba(0,0,0,0.1)",
    marginBottom: "15px"
  },
  formGrid: {
    display: "flex",
    gap: "10px",
    flexWrap: "wrap",
    marginBottom: "10px"
  },
  input: {
    flex: "1 1 150px",
    padding: "10px",
    borderRadius: "8px",
    border: "1px solid #ccc"
  },
  textarea: {
    width: "100%",
    minHeight: "80px",
    padding: "10px",
    borderRadius: "8px",
    border: "1px solid #ccc",
    marginBottom: "10px"
  },
  primaryBtn: {
    background: "#4CAF50",
    color: "#fff",
    padding: "10px 20px",
    border: "none",
    borderRadius: "8px",
    cursor: "pointer"
  },
  editBtn: {
    background: "#2196F3",
    color: "#fff",
    border: "none",
    padding: "6px 12px",
    marginRight: "5px",
    borderRadius: "6px",
    cursor: "pointer"
  },
  deleteBtn: {
    background: "#f44336",
    color: "#fff",
    border: "none",
    padding: "6px 12px",
    borderRadius: "6px",
    cursor: "pointer"
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center"
  },
  subText: {
    color: "#777",
    fontSize: "14px"
  },
  questionBox: {
    marginTop: "10px",
    padding: "10px",
    borderRadius: "8px",
    background: "#f9f9f9"
  },
  question: {
    fontWeight: "bold",
    cursor: "pointer"
  },
  answerBox: {
    width: "100%",
    marginTop: "8px",
    padding: "8px",
    borderRadius: "6px",
    border: "1px solid #ccc"
  }
};